import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, X, Save } from 'lucide-react';
import { Product } from '../types';
import { adminVendorProductApi, productCategoryApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';

interface AdminVendorProductFormProps {
  vendorId: number;
  product: Product | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AdminVendorProductForm({ vendorId, product, onClose, onSuccess }: AdminVendorProductFormProps) {
  const isEdit = Boolean(product);

  const [formData, setFormData] = useState({
    name: '',
    variety: '',
    description: '',
    category: '',
    image: '',
    emoji: '',
  });

  const [categories, setCategories] = useState<{ id: number; name: string; emoji: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await productCategoryApi.getCategories();
        setCategories(data);
        // Set default category if creating new product
        if (!isEdit && data.length > 0) {
          setFormData(prev => ({ ...prev, category: data[0].name }));
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load categories:', apiError);
        setError(apiError.message);
      }
    };
    fetchCategories();
  }, [isEdit]);

  // Load existing product if editing
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        variety: product.variety || '',
        description: product.description || '',
        category: product.category || '',
        image: product.image || '',
        emoji: product.emoji || '',
      });
    }
  }, [product]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    } else if (formData.name.length > 200) {
      errors.name = 'Product name must be 200 characters or less';
    }

    if (!formData.category) {
      errors.category = 'Category is required';
    }

    if (formData.description && formData.description.length > 2000) {
      errors.description = 'Description must be 2000 characters or less';
    }

    if (formData.variety && formData.variety.length > 100) {
      errors.variety = 'Variety must be 100 characters or less';
    }

    if (formData.image && !isValidUrl(formData.image)) {
      errors.image = 'Image must be a valid URL';
    }

    if (formData.emoji && formData.emoji.length > 10) {
      errors.emoji = 'Emoji field is too long';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean up data (remove empty strings, convert to null/undefined)
      const productData = {
        name: formData.name.trim(),
        variety: formData.variety.trim() || undefined,
        description: formData.description.trim() || undefined,
        category: formData.category,
        image: formData.image.trim() || undefined,
        emoji: formData.emoji.trim() || undefined,
      };

      if (isEdit && product) {
        await adminVendorProductApi.updateVendorProduct(vendorId, product.id, productData);
      } else {
        await adminVendorProductApi.createVendorProduct(vendorId, productData);
      }

      onSuccess();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to save product:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="product-form-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900" data-testid="product-form-title">
            {isEdit ? 'Edit Product' : 'Create Product'}
          </h2>
          <button
            onClick={onClose}
            data-testid="close-product-form-button"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6" data-testid="product-form">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start" data-testid="product-form-error">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-800 font-medium">Error</p>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              data-testid="product-name-input"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              maxLength={200}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter product name"
            />
            {validationErrors.name && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">{formData.name.length}/200 characters</p>
          </div>

          {/* Variety */}
          <div className="mb-4">
            <label htmlFor="variety" className="block text-sm font-medium text-gray-700 mb-1">
              Variety
            </label>
            <input
              type="text"
              id="variety"
              value={formData.variety}
              onChange={(e) => handleChange('variety', e.target.value)}
              maxLength={100}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.variety ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Granny Smith, Honeycrisp"
            />
            {validationErrors.variety && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.variety}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">{formData.variety.length}/100 characters</p>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              maxLength={2000}
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Describe your product"
            />
            {validationErrors.description && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.description}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">{formData.description.length}/2000 characters</p>
          </div>

          {/* Category */}
          <div className="mb-4">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              data-testid="product-category-select"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.category ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {cat.emoji} {cat.name}
                </option>
              ))}
            </select>
            {validationErrors.category && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.category}</p>
            )}
          </div>

          {/* Image URL */}
          <div className="mb-4">
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <input
              type="text"
              id="image"
              value={formData.image}
              onChange={(e) => handleChange('image', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.image ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="https://example.com/image.jpg"
            />
            {validationErrors.image && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.image}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Optional: URL to product image</p>
          </div>

          {/* Emoji */}
          <div className="mb-6">
            <label htmlFor="emoji" className="block text-sm font-medium text-gray-700 mb-1">
              Emoji
            </label>
            <input
              type="text"
              id="emoji"
              value={formData.emoji}
              onChange={(e) => handleChange('emoji', e.target.value)}
              maxLength={10}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.emoji ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="🍎"
            />
            {validationErrors.emoji && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.emoji}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Optional: Single emoji to represent the product</p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={loading}
              data-testid="save-product-button"
              className="flex-1 flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  {isEdit ? 'Update Product' : 'Create Product'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              data-testid="cancel-product-form-button"
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
